/**
 * The probes a scene draws with, packed as the texels each lit fragment fetches: Godot's
 * `ReflectionData` (`light_storage.cpp:1785-1824`), one row of `SCENE_ROW_TEXELS` per probe.
 */
import { sRGBToLinearRGB } from '../../utils/colorSpace.js';
import { RowBuffer } from '../rowTexture/packedRows.js';
import { highMaskHalf, lowMaskHalf, SCENE_ROW_TEXELS, setMatrixRows, setTexel, } from '../sceneRows/sceneRowTexels.js';
/**
 * The rows of the drawn probes, rewritten in place by each `pack`. The texels of one probe: the
 * world-to-local matrix's rows, the half size and the blend distance, the origin offset and the
 * intensity, the ambient and its mode, then the mask, the box projection and the atlas layer, then
 * the captured exposure. The mask goes in two 16-bit halves, as a float holds 24 bits exactly.
 */
export class ProbeRows extends RowBuffer {
    /** The row being packed, as wide as a decal's, whose extra texels stay zero. */
    row = new Float32Array(SCENE_ROW_TEXELS * 4);
    constructor() {
        super(SCENE_ROW_TEXELS * 4);
    }
    /** Packs `probes` in order, and answers whether any row changed. */
    pack(probes) {
        this.begin();
        for (const probe of probes) {
            setProbeTexels(this.row, probe);
            this.append(this.row);
        }
        return this.finish();
    }
}
function setProbeTexels(row, probe) {
    const { size, originOffset, ambientColor: colour, ambientColorEnergy: energy, reflectionMask } = probe;
    // Godot stores the ambient colour in linear, times its energy (`light_storage.cpp:1817-1822`).
    const [r, g, b] = sRGBToLinearRGB(colour.r, colour.g, colour.b);
    setMatrixRows(row, 0, probe.worldToLocal.elements);
    setTexel(row, 3, size.x / 2, size.y / 2, size.z / 2, probe.blendDistance);
    setTexel(row, 4, originOffset.x, originOffset.y, originOffset.z, probe.intensity);
    setTexel(row, 5, r * energy, g * energy, b * energy, probe.ambientMode);
    setTexel(row, 6, lowMaskHalf(reflectionMask), highMaskHalf(reflectionMask), probe.boxProjection ? 1 : 0, probe.atlasLayer);
    setTexel(row, 7, probe.capturedExposure, 0, 0, 0);
}
//# sourceMappingURL=probeRows.js.map